import {Button}          from '@/domains/shared/components/Button';
import {Menu}            from '@headlessui/react';
import {ChevronDownIcon} from '@heroicons/react/20/solid';
import autosize          from 'autosize/dist/autosize';
import classNames        from 'classnames';
import {
	forwardRef,
	useEffect,
	useRef,
	useState,
}                        from 'react';

const scoreTypes = {
	positive: {
		label:      'Positive',
		background: 'bg-green-600',
		text:       'text-green-700',
		badge:      'bg-green-50 text-green-700 ring-green-600',
	},
	negative: {
		label:      'Negative',
		background: 'bg-red-600',
		text:       'text-red-700',
		badge:      'bg-red-50 text-red-700 ring-red-600',
	},
	neutral:  {
		label:      'Neutral',
		background: 'bg-yellow-800',
		text:       'text-yellow-800',
		badge:      'bg-yellow-50 text-yellow-700 ring-yellow-600',
	},
	mixed:    {
		label:      'Mixed',
		background: 'bg-blue-600',
		text:       'text-blue-700',
		badge:      'bg-blue-50 text-blue-700 ring-blue-600',
	},
};

function Item({
	              input,
	              output,
	              costs,
              }) {
	return <div className="rounded-lg p-4 bg-teal-50 ring-1 ring-inset ring-teal-500 space-y-4">
		<div className="space-y-2 sm:flex sm:justify-between sm:space-y-0 sm:gap-x-4">
			<div className="italic text-teal-900 whitespace-pre-wrap line-clamp-3 sm:text-sm sm:leading-6">
				{input}
			</div>

			<div className="space-y-1 sm:text-right tabular-nums sm:text-sm sm:leading-6 sm:flex-shrink-0">
				<p className="flex gap-x-2 justify-between">
					<span>Running this request just cost us:</span>
					<span className="font-semibold">{new Intl.NumberFormat('en-US', {
						style:                 'currency',
						currency:              'USD',
						minimumFractionDigits: 4,
					}).format(costs.request)}</span></p>

				<p className="flex gap-x-2 justify-between">
					<span>To date, this demo has cost us:</span>
					<span className="font-semibold">{new Intl.NumberFormat('en-US', {
						style:                 'currency',
						currency:              'USD',
						minimumFractionDigits: 4,
					}).format(costs.total)}</span></p>
			</div>
		</div>

		<div className="space-y-4">
			<div className="flex justify-center">
				<p className={classNames([
					'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset',
					scoreTypes[output.response.Sentiment.toLowerCase()].badge,
				])}
				>
					{output.response.Sentiment}
				</p>
			</div>

			<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
				{Object.entries(scoreTypes)
				       .map(([key, value]) => {
					       return <div className="space-y-2"
					                   key={key}
					       >
						       <div className={classNames([
							       'flex justify-between mb-1 text-sm font-medium',
							       value.text,
						       ])}
						       >
							       <span className="uppercase tracking-wide">
								       {value.label}
							       </span>

							       <span>
								       {Math.round(output.response.SentimentScore[value.label] * 100)}%
							       </span>
						       </div>

						       <div className="w-full bg-teal-900/25 rounded-full overflow-hidden h-2.5">
							       <div className={classNames([
								       'h-2.5 rounded-ful',
								       value.background,
							       ])}
							            style={{width: `${Math.round(output.response.SentimentScore[value.label] * 100)}%`}}
							       ></div>
						       </div>
					       </div>;
				       })}
			</div>
		</div>
	</div>;
}

let CustomButton = forwardRef(function (props, ref) {
	return <Button ref={ref} {...props} />;
});

CustomButton.displayName = 'CustomButton';

export default function SentimentAnalysis() {
	const examples = [
		{
			label: '1 star review',
			value: [
				       'Before I started using this lens on my Canon M5 I thought I would take up the free 3 year warranty that Sigma offer via their online service.',
				       'I entered the correct details and received an e mail back from them stating "Unfortunately this serial number is not on our system as an official UK import."',
				       'Therefore they could not offer the 3 year free warranty. I feel let down by Amazon as this lens is clearly a grey import and other potential buyers should be made aware of this.',
				       'I have returned the lens for a full refund and intend to purchase from an on line photographic dealer.',
			       ].join('\n'),
		},
		{
			label: '2 star review',
			value: [
				       'I\'ve heard great things about this lens. Unfortunately, it doesn\'t connect to my camera.',
				       'According to the instructions the lens and camera were compatible. However, it simply would not connect.',
				       'Hard to use a camera without a lens.',
			       ].join('\n'),
		},
		{
			label: '3 star review',
			value: [
				       'I used this lens extensively on a recent two-week trip with my Canon M50, and was very disappointed with the image quality compared to native Canon lenses with IS.',
				       'Without it, and with such a broad field of focus, I found (after a lot of experimentation) I had to try to compensate for the lack of IS with the f-stop/exposure time settings.',
				       'I lost a lot of good pics--the resolution was often poor--and ended up relying on a basic 15-45mm IS lens for wider shots, or ditching it altogether for my iPhone12 pro max. Frustrated.',
			       ].join('\n'),
		},
		{
			label: '4 star review',
			value: [
				       'This Sigma lens produces really nice photos, especially in low light.',
				       'But it is a really large and heavy lens and I quickly realized that I probably would not reach for it often so I did return it and opted instead for the Canon EF-M 22 mm 2.0 which while not as wide or as fast also produces beautiful photos and adds so little size and weight to my Canon M50 that I know I will reach for it often.',
				       'If the size isn\'t an issue for you, do NOT hesitate to grab this really nice lens. I also got a very nice deal by purchasing a used lens and it was in perfect, like new, in the box condition.',
			       ].join('\n'),
		},
		{
			label: '5 star review',
			value: [
				       'This is a fantastic lens. You will be very pleased with it.',
				       'It has been a joy to use for video and photography. The results speak for themselves.',
				       'My only reservation was price, and it has provided much more value than I expected.',
				       'The lens is not perfect for every shot, but when you can use it, you will be so impressed.',
				       'About my only warning is that it has an extremely distinct “look” to it. It’s not necessarily very true to life, in some ways.',
				       'So while you will get astonishing photos, just keep in mind that they will be somewhat stylized by the lens.',
				       'With that being said, I think the signature of the lens is awesome.',
				       'I find the photos incredible. I only wish I had a longer focal length one.',
			       ].join('\n'),
		},
		{
			label: 'Atlas Client\'s Testimonial',
			value: [
				       'Atlas\'s expertise with building web and mobile apps has revolutionised our tech operations at Labourpower.',
				       'Their impactful solutions and dedicated team have been pivotal in driving our digital transformation journey.',
				       'We are very happy with their services, and would highly recommend them for web and mobile app development.',
			       ].join('\n'),
		},
		{
			label: 'Justin Jackson\'s Testimonial on Radical Design',
			value: [
				       'Bought Jack McDade\'s course on design. Going through it now...and it is SO well done!',
			       ].join('\n'),
		},
		{
			label: 'Never Gonna Give You Up',
			value: [
				       'We\'re no strangers to love',
				       'You know the rules and so do I (do I)',
				       'A full commitment\'s what I\'m thinking of',
				       'You wouldn\'t get this from any other guy',
				       'I just wanna tell you how I\'m feeling',
				       'Gotta make you understand',
				       'Never gonna give you up',
				       'Never gonna let you down',
				       'Never gonna run around and desert you',
				       'Never gonna make you cry',
				       'Never gonna say goodbye',
				       'Never gonna tell a lie and hurt you',
				       'We\'ve known each other for so long',
				       'Your heart\'s been aching, but you\'re too shy to say it (say it)',
				       'Inside, we both know what\'s been going on (going on)',
				       'We know the game and we\'re gonna play it',
				       'And if you ask me how I\'m feeling',
				       'Don\'t tell me you\'re too blind to see',
				       'Never gonna give you up',
				       'Never gonna let you down',
				       'Never gonna run around and desert you',
				       'Never gonna make you cry',
				       'Never gonna say goodbye',
				       'Never gonna tell a lie and hurt you',
				       'Never gonna give you up',
				       'Never gonna let you down',
				       'Never gonna run around and desert you',
				       'Never gonna make you cry',
				       'Never gonna say goodbye',
				       'Never gonna tell a lie and hurt you',
				       'We\'ve known each other for so long',
				       'Your heart\'s been aching, but you\'re too shy to say it (to say it)',
				       'Inside, we both know what\'s been going on (going on)',
				       'We know the game and we\'re gonna play it',
				       'I just wanna tell you how I\'m feeling',
				       'Gotta make you understand',
				       'Never gonna give you up',
				       'Never gonna let you down',
				       'Never gonna run around and desert you',
				       'Never gonna make you cry',
				       'Never gonna say goodbye',
				       'Never gonna tell a lie and hurt you',
				       'Never gonna give you up',
				       'Never gonna let you down',
				       'Never gonna run around and desert you',
				       'Never gonna make you cry',
				       'Never gonna say goodbye',
				       'Never gonna tell a lie and hurt you',
				       'Never gonna give you up',
				       'Never gonna let you down',
				       'Never gonna run around and desert you',
				       'Never gonna make you cry',
				       'Never gonna say goodbye',
				       'Never gonna tell a lie and hurt you',
			       ].join('\n'),
		},
	];

	const [input, setInput] = useState(examples[examples.length - 1].value.substring(0, 300));
	const [items, setItems] = useState([]);
	const textarea          = useRef(null);

	const submit = async (e) => {
		e.preventDefault();
		e.stopPropagation();

		const response = await fetch('https://demos.atlas.dev/api/comprehend/detect-sentiment', {
			method:  'POST',
			headers: {
				'Accept':       'application/json',
				'Content-Type': 'application/json',
			},
			body:    JSON.stringify({
				text: input,
			}),
		});

		const payload = await response.json();

		if (response.status !== 200) {
			alert(payload.message);
		} else {
			setItems((currentItems) => {
				return [
					...currentItems,
					payload,
				];
			});
		}
	};

	useEffect(() => {
		if (textarea.current) {
			autosize(textarea.current);
		}
	}, [
		textarea,
	]);

	useEffect(() => {
		if (textarea.current) {
			autosize.update(textarea.current);
		}
	}, [
		input,
	]);

	return <div className={classNames('not-prose mb-12 space-y-4')}>
		<form onSubmit={submit}
		      action="#"
		      className="relative group"
		>
			<div className="overflow-hidden rounded-lg shadow-sm ring-1 ring-inset ring-teal-500 focus-within:ring-2 focus-within:ring-teal-600 bg-teal-50/50">
				<label htmlFor="input"
				       className="sr-only"
				>
					Input
				</label>

				<textarea value={input}
				          onChange={e => setInput(e.target.value.substring(0, 300))}
				          rows={3}
				          id="input"
				          className="block w-full resize-none border-0 bg-transparent p-3 text-teal-900 placeholder:text-teal-800 focus:ring-0 sm:text-sm sm:leading-6"
				          placeholder="Add some text to detect sentiment..."
				          maxLength={300}
				          ref={textarea}
				/>

				{/* Spacer element to match the height of the toolbar */}
				<div className="py-3"
				     aria-hidden="true"
				>
					{/* Matches height of button in toolbar (1px border + 36px content height) */}
					<div className="py-px">
						<div className="h-9" />
					</div>
				</div>
			</div>

			<div className="absolute inset-x-0 bottom-0 flex justify-between p-3">
				<div className="relative">
					<Menu as="div"
					      className="relative inline-block text-left"
					>
						<Menu.Button as={CustomButton}>
							Examples

							<ChevronDownIcon
								className="-mr-1 ml-2 h-5 w-5"
								aria-hidden="true"
							/>
						</Menu.Button>

						<Menu.Items className="isolate z-10 absolute left-0 mt-2 w-64 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none divide-y divide-gray-200">
							{examples.map((example, index) => {
								return (<div className="p-2"
								             key={index}
								>
									<Menu.Item>
										{({active}) => (<button
											type="button"
											onClick={(e) => setInput(example.value.substring(0, 300))}
											className={`${active ? 'bg-teal-500 text-white' : 'text-gray-900'} group flex w-full items-center justify-start text-left rounded-md px-2 py-2 text-sm`}
										>
											{example.label}
										</button>)}
									</Menu.Item>
								</div>);
							})}
						</Menu.Items>
					</Menu>
				</div>

				<div className="flex-shrink-0 flex justify-end items-center gap-x-4">
					<p className="text-sm font-light text-teal-900">
						{input.length} / 300
					</p>

					<Button type="submit"
					        color="gray"
					        className="animate-pulse group-focus-within:animate-none"
					>
						Detect Sentiment
					</Button>
				</div>
			</div>
		</form>

		{items.length > 0 && <ul className="space-y-4 max-h-[50vh] overflow-auto">
			{items.slice()
			      .reverse()
			      .map((item, index) => {
				      return <Item {...item} key={index} />;
			      })}
		</ul>}
	</div>;
}